// src/websocket/subscription.ts
import { EventEmitter } from 'eventemitter3';
import { get_channel_config } from './helper_functions';
export class BaseSubscription extends EventEmitter {
    constructor(channelConfig, websocketHandler, process = "subscribe") {
        super();
        this.isSubscribed = false;
        this.isListening = false;
        this.messageBuffer = new Map();
        this.pendingAcks = new Map();
        this.ACK_TIMEOUT = 50000;
        this.messageCount = 0;
        this.websocketHandler = websocketHandler;
        this.channelConfig = channelConfig;
        if (process === "subscribe") {
            this.isSubscribed = true;
        }
        else if (process === "presence") {
            this.isListening = true;
        }
    }
    async validateSubscription(process) {
        try {
            if (["art_config", "art_secure"].includes(this.channelConfig.channelName)) {
                return;
            }
            await get_channel_config(this.channelConfig.channelName, process, this.websocketHandler).then((config) => {
                this.channelConfig = config;
            }).catch((error) => {
                console.error(`${error}`);
            });
        }
        catch (error) {
            console.error(`${error}`);
        }
    }
    /**
     * Check for presence on the channel.
     * @param callback Callback to handle the presence data.
     * @throws Error if not subscribed for presence.
     */
    async fetchPresence(callback) {
        await this.presence();
        if (!this.isListening) {
            throw new Error("Not subscribed for presence");
        }
        let previousPresenceData = null;
        this.on('art_presence', ({ usernames, error }) => {
            if (!error) {
                previousPresenceData = usernames;
                callback(usernames);
            }
        });
        callback(previousPresenceData);
    }
    acknowledge(request, res) {
        if (this.channelConfig.channelType !== "targeted" || this.channelConfig.channelName !== "secure") {
            return;
        }
        const { id, from, channel, namespace, to, pipeline_id, attempt_id, interceptor_name, to_username, ref_id } = request;
        if (["art_config", "art_secure", "art_presence"].includes(channel)) {
            return;
        }
        const response = {
            channel: channel,
            namespace: namespace,
            id: id,
            ref_id,
            from,
            to_username,
            to,
            return_flag: res,
            pipeline_id: pipeline_id,
            interceptor_name: interceptor_name,
            attempt_id: attempt_id,
        };
        this.websocketHandler.sendMessage(JSON.stringify(response));
    }
    async handleMessageAcks(event, return_flag, data) {
        if (return_flag === 'SA' && data.ref_id) {
            const entry = this.pendingAcks.get(data.ref_id);
            if (entry) {
                clearTimeout(entry.timer);
                entry.resolve(data.ref_id);
                this.pendingAcks.delete(data.ref_id);
                return;
            }
        }
    }
    async subscribe() {
        if (["art_config", "art_secure"].includes(this.channelConfig.channelName)) {
            return;
        }
        this.isSubscribed = true;
        await get_channel_config(this.channelConfig.channelName, "subscribe", this.websocketHandler).then((config) => {
            this.channelConfig = config;
        }).catch((error) => {
            console.error(`${error}`);
            this.isSubscribed = false;
        });
    }
    reconnect() {
        if (this.channelConfig.channelName !== "art_config" && this.channelConfig.channelName !== "art_secure") {
            if (this.isListening) {
                this.validateSubscription("presence");
                this.presence();
            }
            if (this.isSubscribed) {
                this.subscribe();
            }
        }
    }
    async presence() {
        if (["art_config", "art_secure"].includes(this.channelConfig.channelName)) {
            return;
        }
        this.isListening = true;
        await get_channel_config(this.channelConfig.channelName, "presence", this.websocketHandler).then((config) => {
            this.channelConfig = config;
            this.push('art_presence', {});
        }).catch((error) => {
            this.isListening = false;
            throw new Error(`${error}`);
        });
    }
    /**
     * Push a message to the channel with a specific event and data.
     * @param event The event name.
     * @param data The data to send.
     */
    async push(event, data, options) {
        var _a, _b, _c;
        await this.websocketHandler.wait();
        const connection = this.websocketHandler.getConnection();
        let to = (_a = options === null || options === void 0 ? void 0 : options.to) !== null && _a !== void 0 ? _a : [];
        let messageStr = JSON.stringify(data);
        if (this.channelConfig.channelType == "secure" || this.channelConfig.channelType == "targeted") {
            if ((!to || to.length !== 1) && event !== 'art_presence') {
                throw new Error("Exactly one user must be specified for sending message.");
            }
        }
        if (this.channelConfig.channelType == "secure" && event !== 'art_presence') {
            this.websocketHandler.pushForSecureLine("secured_public_key", {
                "username": to[0]
            });
            let receipientPublicKey = await this.websocketHandler.listenForSecureLine('secured_public_key', (sdata) => {
                if (sdata['username'] !== to[0]) {
                    return false;
                }
                if (sdata['status'] == 'unsuccessfull') {
                    throw Error(sdata['error']);
                }
                return true;
            });
            messageStr = await this.websocketHandler.encrypt(messageStr, receipientPublicKey['public_key']);
        }
        let refId;
        let ackPromise = Promise.resolve(undefined);
        if (!["art_config", "art_secure", "art_presence"].includes(this.channelConfig.channelName)) {
            this.messageCount++;
            refId = `${(_b = this.websocketHandler.getConnection()) === null || _b === void 0 ? void 0 : _b.ConnectionId}_${this.channelConfig.channelName}_${this.messageCount}`;
            ackPromise = new Promise((resolve, reject) => {
                const timer = setTimeout(() => {
                    this.pendingAcks.delete(refId);
                    reject(new Error('ACK timeout'));
                }, this.ACK_TIMEOUT);
                if (this.channelConfig.channelType === "targeted" || this.channelConfig.channelName === "secure") {
                    this.pendingAcks.set(refId, { resolve, reject, timer });
                }
                else {
                    resolve(refId);
                }
            });
        }
        const message = {
            from: (_c = connection === null || connection === void 0 ? void 0 : connection.ConnectionId) !== null && _c !== void 0 ? _c : "",
            to: to,
            channel: this.channelConfig.channelName,
            event: event,
            content: messageStr,
            ref_id: refId
        };
        this.websocketHandler.sendMessage(JSON.stringify(message));
        return ackPromise;
    }
    handleMessage(event, payload) { }
}
